// The report widget-type catalog: the editor-facing inverse of the inline
// visuals registry. Each entry pins the args that select a given visual and
// declares which secondary controls the edit form should expose for it.
// Colocated with the Reports edit UI that consumes it.
//
// CONTRACT DUPLICATION (known debt): the `fixed` tuples mirror the REGISTRY
// match() functions in ui/components/AiAssistant/visuals/index.ts and the
// get_entity_data enums in ai-services/assistant/tools.py
// (GET_ENTITY_DATA_SCHEMA). Keep them in sync when a widget type is added or a
// match() condition changes. `matchWidgetType` reproduces the registry's
// precedence so the form classifies a config the same way the grid renders it.

// entity: 'brand' | 'category' | 'either' | 'optional' | 'none'
//   'brand'/'category' pin entity_type; 'either' requires an entity of either
//   type; 'optional' allows an entity-less call (TikTok hashtags across all
//   categories); 'none' is always entity-less — the standalone list types,
//   executable once entity_ids has at least one pin, and matched only when
//   the args carry no entity_type/entity_id (mirroring the registry's
//   standalone matchers).
// relatedEntity: true for pair-shaped types (TikTok related posts, Google
//   related search queries) that also require a second entity, stored as
//   `related_entity_id`.
// pinnedEntity: the kind ('brand' | 'category' | 'hashtag') a type's pinned
//   list selects from. pop_index types pin by id via `entity_ids`; TikTok
//   hashtags pin by NAME via `hashtags`. The backend rejects either key on any
//   other shape.
// canChart: the type takes the `aggregate` / `trend_lines` view flags once it
//   has pins. The backend rejects them on the anchored pop_index pins.
// exclude: the type's rows can be dropped by name. Top-N shapes only, and
//   NOT the complement of pinnedEntity. Mutually exclusive with a pin list
//   (the backend 400s). Mirrors `isExcludableListInput` in lib/listInputs.ts.
// pinnedDepthsExcluded: category depths the pinned-entity picker must not
//   offer (category_list excludes 'cat1': industries are the index baseline,
//   constant 100 — the backend rejects them too; subcategories keeps only
//   'cat5': the backend intersects its pins with the anchor's leaf
//   descendants, so a shallower pin can never produce a row).
export const WIDGET_TYPES = [
    {
        id: 'brand_overview',
        label: 'Brand overview',
        fixed: {
            data_source: 'pop_index',
            entity_type: 'brand',
            entity_relationship: 'self',
        },
        entity: 'brand',
        controls: ['analysis_lens'],
        defaults: { analysis_lens: 'overview' },
    },
    {
        id: 'category_overview',
        label: 'Category overview',
        fixed: {
            data_source: 'pop_index',
            entity_type: 'category',
            entity_relationship: 'self',
        },
        entity: 'category',
        controls: ['analysis_lens'],
        defaults: { analysis_lens: 'overview' },
    },
    {
        id: 'top_brands',
        label: 'Top brands',
        fixed: {
            data_source: 'pop_index',
            entity_type: 'category',
            entity_relationship: 'brands',
        },
        entity: 'category',
        pinnedEntity: 'brand',
        controls: ['entity_ids', 'exclude', 'order_by', 'life_cycle', 'price'],
        defaults: {},
    },
    {
        id: 'subcategories',
        label: 'Subcategories',
        fixed: {
            data_source: 'pop_index',
            entity_type: 'category',
            entity_relationship: 'cat5s',
        },
        entity: 'category',
        pinnedEntity: 'category',
        pinnedDepthsExcluded: ['cat1', 'cat2', 'cat3', 'cat4'],
        controls: ['entity_ids', 'exclude', 'order_by', 'life_cycle'],
        defaults: {},
    },
    {
        id: 'related_categories',
        label: 'Related categories',
        // entity_type is intentionally not pinned (brand or category).
        fixed: {
            data_source: 'pop_index',
            entity_relationship: 'related_categories',
        },
        entity: 'either',
        pinnedEntity: 'category',
        controls: ['entity_ids', 'exclude', 'order_by', 'affinity_group'],
        defaults: {},
    },
    // Must come after top_brands so matchWidgetType chooses correctly
    {
        id: 'brand_list',
        label: 'Brand list',
        fixed: { data_source: 'pop_index', entity_relationship: 'brands' },
        entity: 'none',
        pinnedEntity: 'brand',
        canChart: true,
        controls: ['entity_ids', 'order_by', 'life_cycle'],
        defaults: {},
    },
    {
        id: 'category_list',
        label: 'Category list',
        fixed: { data_source: 'pop_index', entity_relationship: 'categories' },
        entity: 'none',
        pinnedEntity: 'category',
        canChart: true,
        pinnedDepthsExcluded: ['cat1'],
        controls: ['entity_ids', 'order_by', 'life_cycle'],
        defaults: {},
    },
    {
        id: 'tiktok_hashtags',
        label: 'TikTok hashtags',
        fixed: { data_source: 'tiktok', entity_relationship: 'hashtags' },
        entity: 'optional',
        pinnedEntity: 'hashtag',
        canChart: true,
        controls: [
            'hashtags',
            'exclude',
            'preset',
            'order_by',
            'branded',
            'filters',
        ],
        defaults: {},
    },
    {
        id: 'tiktok_creators',
        label: 'TikTok creators',
        fixed: { data_source: 'tiktok', entity_relationship: 'creators' },
        entity: 'either',
        controls: ['order_by'],
        defaults: {},
    },
    {
        id: 'tiktok_posts',
        label: 'TikTok posts',
        fixed: { data_source: 'tiktok', entity_relationship: 'posts' },
        entity: 'either',
        controls: ['order_by'],
        defaults: {},
    },
    {
        id: 'tiktok_related_posts',
        label: 'TikTok related posts',
        fixed: { data_source: 'tiktok', entity_relationship: 'related_posts' },
        entity: 'either',
        relatedEntity: true,
        controls: ['order_by'],
        defaults: {},
    },
    {
        id: 'google_related_terms',
        label: 'Google related search queries',
        fixed: { data_source: 'google', entity_relationship: 'related_terms' },
        entity: 'either',
        relatedEntity: true,
        controls: ['exclude'],
        defaults: {},
    },
];

export const WIDGET_TYPE_BY_ID = Object.fromEntries(
    WIDGET_TYPES.map((t) => [t.id, t]),
);

// Every secondary param a type may own — the ones reconcile drops when they're
// not in the next type's `controls`. The structural params (data_source,
// entity_type, entity_relationship) and entity_id/entity_route/geo are handled
// separately.
const SECONDARY_PARAMS = [
    'order_by',
    'preset',
    'branded',
    'filters',
    'analysis_lens',
    'affinity_group',
    'life_cycle',
    'price',
    'entity_ids',
    'hashtags',
    'exclude',
];

/**
 * Classify a widget config's args into a catalog type, reproducing the visuals
 * registry's match precedence. Returns the type object, or null when no widget
 * can render the args (e.g. an unknown data source or relationship).
 */
export const matchWidgetType = (args) => {
    if (!args) return null;
    const dataSource = args.data_source ?? 'pop_index';
    const relationship = args.entity_relationship ?? 'self';
    for (const type of WIDGET_TYPES) {
        const { fixed } = type;
        if (fixed.data_source !== dataSource) continue;
        if (fixed.entity_relationship !== relationship) continue;
        if (fixed.entity_type != null && fixed.entity_type !== args.entity_type)
            continue;
        if (
            type.entity === 'none' &&
            (args.entity_type != null || args.entity_id != null)
        )
            continue;
        return type;
    }
    return null;
};

/**
 * Produce the next draft args when the user picks a widget type: drop secondary
 * params the new type doesn't expose, apply its fixed params + defaults, and
 * reconcile the entity (clearing it when the new type pins an incompatible
 * entity_type). `geo` and any still-valid entity are preserved.
 */
export const reconcileArgsForType = (prevArgs = {}, type) => {
    if (!type) return { ...prevArgs };
    const next = { ...prevArgs };

    // Drop secondary params not exposed by the new type.
    for (const param of SECONDARY_PARAMS) {
        if (!type.controls.includes(param)) delete next[param];
    }

    // order_by is the one secondary param shared across relationships, but each
    // relationship has its own disjoint sort set — so a changed relationship
    // invalidates the carried sort. Drop it (the form re-offers a valid one).
    // A pin list belongs to one relationship, so it is invalidated too.
    const prevRelationship = prevArgs.entity_relationship ?? 'self';
    if (prevRelationship !== type.fixed.entity_relationship) {
        delete next.order_by;
        delete next.entity_ids;
        delete next.hashtags;
        delete next.aggregate;
        delete next.trend_lines;
        delete next.exclude;
    }

    // The backend rejects the view flags on a type that can't chart.
    if (!type.canChart) {
        delete next.aggregate;
        delete next.trend_lines;
    }

    // Clear a carried entity whose type the new widget can't use.
    const pinnedType = type.fixed.entity_type;
    if (
        type.entity === 'none' ||
        (pinnedType &&
            prevArgs.entity_type &&
            prevArgs.entity_type !== pinnedType)
    ) {
        delete next.entity_id;
        delete next.entity_route;
        delete next.entity_type;
    }

    // The related entity is structural to pair-shaped types (like entity_id, not
    // a SECONDARY_PARAM) — drop it when the new type isn't pair-shaped.
    if (!type.relatedEntity) delete next.related_entity_id;

    // Apply the type's fixed params (data_source, entity_relationship, and
    // entity_type when pinned) then its defaults.
    Object.assign(next, type.fixed, type.defaults);
    return next;
};

/**
 * Light "is there anything to fetch yet" guard for the live preview — NOT combo
 * validation. False until the args resolve to a renderable type and (unless the
 * type is entity-optional) carry an entity. Invalid-but-complete combos still
 * fetch and surface the backend's error in the preview tile.
 */
export const isExecutableArgs = (args) => {
    const type = matchWidgetType(args);
    if (!type) return false;
    if (type.relatedEntity && !args?.related_entity_id) return false;
    if (type.entity === 'none') return Boolean(args?.entity_ids?.length);
    if (type.entity === 'optional') return true;
    return Boolean(args?.entity_id);
};
